import React, { createContext, useContext, useState, useEffect } from 'react';
import {
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  createUserWithEmailAndPassword,
  updateProfile,
  sendPasswordResetEmail
} from 'firebase/auth';
import { auth, db } from '../config/firebase';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { sendCustomPasswordReset } from '../services/emailService';
import { useStatusConfig } from '../hooks/useStatusConfig';
import { useDistricts } from '../hooks/useDistricts';

const AuthContext = createContext();

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Shared, cached data available to all consumers via useAuth()
  const { statusConfig, loading: statusLoading } = useStatusConfig();
  const { districts, districtNames, districtsByName, loading: districtsLoading } = useDistricts();

  function login(email, password) {
    return signInWithEmailAndPassword(auth, email, password);
  }

  function logout() {
    return signOut(auth);
  }

  function signup(email, password, displayName) {
    return createUserWithEmailAndPassword(auth, email, password)
      .then((userCredential) => {
        // Update the user's profile with the display name
        return updateProfile(userCredential.user, {
          displayName: displayName
        });
      });
  }

  async function resetPassword(email) {
    try {
      // Try custom branded email first (if functions are deployed and configured)
      await sendCustomPasswordReset(email);
      return { success: true, method: 'custom' };
    } catch (error) {
      console.warn('Custom password reset not available, using Firebase default:', error.message);
      // Fallback to Firebase's built-in password reset
      await sendPasswordResetEmail(auth, email);
      return { success: true, method: 'firebase' };
    }
  }

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        try {
          // Force a token refresh FIRST so custom claims (role, districtIds) are
          // present on the token before any Firestore reads that need them.
          await user.getIdToken(true);

          // Get user data from Firestore to include role information
          const usersRef = collection(db, 'users');
          const q = query(usersRef, where('uid', '==', user.uid));
          const querySnapshot = await getDocs(q);

          if (!querySnapshot.empty) {
            const userData = querySnapshot.docs[0].data();

            // Merge Firebase auth user with Firestore user data
            setCurrentUser({
              ...user,
              role: userData.role || 'Candidate', // Default to Candidate if no role specified
              active: userData.active !== false, // Default to active if not specified
              displayName: userData.displayName || user.displayName,
              conferenceId: userData.conferenceId,
              districtId: userData.districtId,
              districtIds: userData.districtIds || []
            });
          } else {
            // If user doesn't exist in Firestore, use Firebase auth user
            setCurrentUser({
              ...user,
              role: 'Candidate', // Default role
              active: true
            });
          }
        } catch (error) {
          console.error('Error fetching user data:', error);
          // If there's an error, still set the user but without role information
          setCurrentUser({
            ...user,
            role: 'Candidate', // Default role
            active: true
          });
        }
      } else {
        setCurrentUser(null);
      }
      
      setLoading(false);
    });
    
    return unsubscribe;
  }, []);

  const value = {
    currentUser,
    login,
    logout,
    signup,
    resetPassword,
    error,
    setError,
    // Shared lookup data — available to all components without extra fetches
    statusConfig,
    districts,
    districtNames,
    districtsByName,
  };

  const appReady = !loading && !statusLoading && !districtsLoading;

  return (
    <AuthContext.Provider value={value}>
      {appReady && children}
    </AuthContext.Provider>
  );
}
